/** https://ui.shadcn.com/docs/primitives/button */

import { type VariantProps, cva } from "class-variance-authority";
import LoaderIcon from "../icons/loader";
import { cn } from "../";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-hidden disabled:opacity-40",
  {
    variants: {
      variant: {
        branded:
          "bg-[var(--branding-button-color,#0f172a)] hover:bg-[var(--branding-button-hover-color,#334155)] border border-[var(--branding-button-color,#0f172a)] text-[var(--branding-button-text-color,#ffffff)]",
        default: "bg-slate-900 border hover:border-slate-700 border-gray-900 text-white hover:bg-slate-700",
        error: "bg-red-500 border border-red-500 hover:border-red-600 text-white hover:bg-red-600 focus:border-red-400",
        success:
          "bg-green-500 border border-green-500 hover:border-green-600 text-white hover:bg-green-600 focus:border-green-400",
        destructive:
          "bg-red-500 border border-red-500 hover:border-red-600 text-white hover:bg-red-600 focus:border-red-400",
        outline: "bg-white border border-gray-900/20 hover:bg-slate-100 shadow-2xs focus:border-slate-400",
        outlineBranded:
          "bg-transparent border border-[var(--branding-button-color,#0f172a)] hover:bg-slate-100 text-[var(--branding-button-color,#0f172a)] focus:[var(--branding-button-color,#0f172a)]",
        secondary: "bg-white border border-gray-900/20 hover:bg-slate-900 hover:text-white shadow-2xs",
        subtle:
          "bg-slate-900/10 hover:border-slate-900/20 border border-slate-900/10 text-gray-700 hover:text-gray-900 hover:bg-slate-900/20",
        ghost: "bg-transparent border border-transparent hover:bg-slate-900/10 focus:border-slate-400",
        link: "bg-transparent text-blue-500 cursor-pointer hover:underline focus:underline focus:border-slate-400 px-0 py-0",
        linkBranded:
          "bg-transparent text-[var(--branding-button-color,#0f172a)] cursor-pointer hover:underline focus:underline focus:border-slate-400",
        magic: "bg-purple-50 text-purple-700/70 border border-purple-700/70",
        row: "group -mx-2 relative font-normal rounded-md py-1 px-2 flex flex-row gap-x-2 items-center transition-colors hover:bg-blue-50 data-[state=selected]:bg-blue-50",
      },
      size: {
        default: "h-9 py-2 px-3",
        sm: "px-2 py-1 h-auto text-sm font-normal rounded-[0.25rem]",
        lg: "h-12 px-8 rounded-md",
        inline: "h-auto inline",
        uniformDefault: "h-9 w-9",
        uniformLg: "h-12 w-12",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  isActive?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
}

const Button = ({ className, variant, size, children, loading, isActive, ...props }: ButtonProps) => {
  return (
    <button
      {...props}
      className={cn(
        "flex items-center gap-2",
        buttonVariants({ variant, size, className }),
        loading && "pointer-events-none",
        props.disabled && "cursor-not-allowed",
        isActive && "bg-slate-900/10",
      )}
    >
      {loading && <LoaderIcon className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
};

Button.displayName = "Button";

export { Button, buttonVariants };
